'use client';

import React from 'react';
import { MigrationTestSuiteResult, MigrationTestResult } from '@/server/migrate/__tests__/migration-test-suite';

interface MigrationTestResultsProps {
  results: MigrationTestSuiteResult | null;
  loading: boolean;
}

export function MigrationTestResults({ results, loading }: MigrationTestResultsProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 dark:border-blue-400 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Loading...
            </span>
          </div>
          <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-200">Running Migration Tests...</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">This may take a minute</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No Test Results</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Click the test button to start</p>
      </div>
    );
  }

  // Handle "no test data" scenario
  if (results.totalTests === 0 && results.summary.criticalIssues.length > 0) {
    return (
      <div className="rounded-lg border-2 border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 p-8">
        <div className="flex items-start space-x-4">
          <svg className="h-12 w-12 text-yellow-600 dark:text-yellow-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.082 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">No Test Data Available</h3>
            <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-200">{results.summary.criticalIssues[0]}</p>
            
            <div className="mt-4 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 p-4 border border-yellow-200 dark:border-yellow-800">
              <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-2">💡 To enable testing:</h4>
              <ul className="list-inside list-decimal space-y-1 text-sm text-yellow-800 dark:text-yellow-200">
                {results.summary.recommendations.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
            
            <div className="mt-4 text-xs text-yellow-700 dark:text-yellow-300">
              <p className="font-medium">Currently available test data:</p>
              <p>• 🇨🇦 Canada → 🇺🇸 USA, 🇬🇧 UK, 🇫🇷 France</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (passed: boolean) => {
    return passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusIcon = (passed: boolean) => {
    if (passed) {
      return (
        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className={`rounded-lg border-2 p-6 ${results.summary.overallPassed ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon(results.summary.overallPassed)}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {results.summary.overallPassed ? 'All Tests Passed! ✅' : 'Some Tests Failed ❌'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Completed in {(results.totalDuration / 1000).toFixed(2)}s
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {results.passed}/{results.totalTests}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Tests Passed</div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-white dark:bg-gray-800 p-4 text-center shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{results.passed}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Passed</div>
          </div>
          <div className="rounded-lg bg-white dark:bg-gray-800 p-4 text-center shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{results.failed}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Failed</div>
          </div>
          <div className="rounded-lg bg-white dark:bg-gray-800 p-4 text-center shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{results.warnings}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Warnings</div>
          </div>
        </div>

        {/* Critical Issues */}
        {results.summary.criticalIssues.length > 0 && (
          <div className="mt-6 rounded-lg bg-red-100 dark:bg-red-900/30 p-4 border border-red-200 dark:border-red-800">
            <h3 className="font-semibold text-red-900 dark:text-red-200">Critical Issues:</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-red-800 dark:text-red-300">
              {results.summary.criticalIssues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {results.summary.recommendations.length > 0 && (
          <div className="mt-4 rounded-lg bg-blue-100 dark:bg-blue-900/30 p-4 border border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-blue-900 dark:text-blue-200">Recommendations:</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-blue-800 dark:text-blue-300">
              {results.summary.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Individual Test Results */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Detailed Test Results</h3>
        {results.testResults.map((test, index) => (
          <TestResultCard key={index} test={test} />
        ))}
      </div>
    </div>
  );
}

function TestResultCard({ test }: { test: MigrationTestResult }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className={`rounded-lg border-2 ${test.passed ? 'border-green-200 dark:border-green-800 bg-white dark:bg-gray-800' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {test.passed ? (
              <svg className="h-6 w-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{test.testName}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {test.duration}ms • {test.errors.length} errors • {test.warnings.length} warnings
              </p>
            </div>
          </div>
          <svg
            className={`h-5 w-5 transform text-gray-400 dark:text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          {/* Errors */}
          {test.errors.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-2 font-semibold text-red-900 dark:text-red-200">Errors:</h5>
              <ul className="list-inside list-disc space-y-1 text-sm text-red-800 dark:text-red-300">
                {test.errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {test.warnings.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-2 font-semibold text-yellow-900 dark:text-yellow-200">Warnings:</h5>
              <ul className="list-inside list-disc space-y-1 text-sm text-yellow-800 dark:text-yellow-300">
                {test.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Test Details */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Price Conversion */}
            {test.details.priceConversion && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-200 dark:border-gray-600">
                <h5 className="mb-2 flex items-center font-semibold text-gray-900 dark:text-gray-100">
                  <span className="mr-2">💱</span>
                  Price Conversion
                  {test.details.priceConversion.passed ? (
                    <span className="ml-2 text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="ml-2 text-red-600 dark:text-red-400">✗</span>
                  )}
                </h5>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="font-medium">Expected Currency:</span> {test.details.priceConversion.expectedCurrency}
                  </p>
                  <p>
                    <span className="font-medium">Converted Prices:</span>{' '}
                    {test.details.priceConversion.convertedPrices.length > 0
                      ? test.details.priceConversion.convertedPrices.slice(0, 3).join(', ')
                      : 'None'}
                    {test.details.priceConversion.convertedPrices.length > 3 && '...'}
                  </p>
                </div>
              </div>
            )}

            {/* Product Count */}
            {test.details.productCount && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-200 dark:border-gray-600">
                <h5 className="mb-2 flex items-center font-semibold text-gray-900 dark:text-gray-100">
                  <span className="mr-2">📦</span>
                  Product Count
                  {test.details.productCount.passed ? (
                    <span className="ml-2 text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="ml-2 text-red-600 dark:text-red-400">✗</span>
                  )}
                </h5>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="font-medium">Original:</span> {test.details.productCount.original}
                  </p>
                  <p>
                    <span className="font-medium">Migrated:</span> {test.details.productCount.migrated}
                  </p>
                </div>
              </div>
            )}

            {/* Metadata */}
            {test.details.metadata && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-200 dark:border-gray-600">
                <h5 className="mb-2 flex items-center font-semibold text-gray-900 dark:text-gray-100">
                  <span className="mr-2">📝</span>
                  Metadata
                  {test.details.metadata.passed ? (
                    <span className="ml-2 text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="ml-2 text-red-600 dark:text-red-400">✗</span>
                  )}
                </h5>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="font-medium">Fields Checked:</span> {test.details.metadata.fieldsChecked.length}
                  </p>
                  {test.details.metadata.issues.length > 0 && (
                    <p className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Issues:</span> {test.details.metadata.issues.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Href Transformation */}
            {test.details.hrefTransformation && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-200 dark:border-gray-600">
                <h5 className="mb-2 flex items-center font-semibold text-gray-900 dark:text-gray-100">
                  <span className="mr-2">🔗</span>
                  Href Transformation
                  {test.details.hrefTransformation.passed ? (
                    <span className="ml-2 text-green-600 dark:text-green-400">✓</span>
                  ) : (
                    <span className="ml-2 text-red-600 dark:text-red-400">✗</span>
                  )}
                </h5>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="font-medium">Locales:</span> {test.details.hrefTransformation.transformedLocales.join(', ')}
                  </p>
                  {test.details.hrefTransformation.issues.length > 0 && (
                    <p className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Issues:</span> {test.details.hrefTransformation.issues.length}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Text Transformation Examples */}
          {test.details.textTransformation && test.details.textTransformation.examples.length > 0 && (
            <div className="mt-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 p-4 border border-blue-200 dark:border-blue-800">
              <h5 className="mb-2 font-semibold text-blue-900 dark:text-blue-200">📖 Text Transformation Examples:</h5>
              <div className="space-y-2">
                {test.details.textTransformation.examples.slice(0, 2).map((example, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-200">{example.field}:</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Before:</span> {example.original}
                    </p>
                    <p className="text-blue-700 dark:text-blue-300">
                      <span className="font-medium">After:</span> {example.transformed}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

